import { getEnv } from '@/lib/env';

import type {
  OpenApiDataFor,
  OpenApiEnvelopeFor,
  OpenApiMethodsFor,
  OpenApiRequestOptions,
  OpenApiRoute,
  OpenApiRoutesFor,
} from './generated/route-types';
import { readCsrfToken } from './csrf';
import type { ApiErrorBody, FlattenedIssues } from './types';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  readonly requestId: string | undefined;

  constructor(
    status: number,
    code: string,
    message: string,
    details?: unknown,
    requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }

  /** Field errors from a Zod `flatten()` VALIDATION_ERROR, when present. */
  get fieldErrors(): Record<string, string[]> {
    const details = this.details as Partial<FlattenedIssues> | undefined;
    const out: Record<string, string[]> = {};
    if (details && typeof details === 'object' && details.fieldErrors) {
      for (const [key, value] of Object.entries(details.fieldErrors)) if (value) out[key] = value;
    }
    return out;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export type RequestOptions = {
  method?: Method;
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  /** Guest booking access proof (x-booking-access-token). */
  bookingAccessToken?: string | null;
  /** Waitlist entry token (x-waitlist-access-token). */
  waitlistAccessToken?: string | null;
  /** Upload completion token (x-upload-token). */
  uploadToken?: string | null;
  signal?: AbortSignal;
};

type GetRoute = OpenApiRoutesFor<'GET'>;

/** Type-safe request overload: the route determines method, JSON body, query, and response. */
export function apiRequest<Path extends GetRoute>(
  path: Path,
  options?: OpenApiRequestOptions<Path, 'GET', RequestOptions>,
): Promise<OpenApiDataFor<Path, 'GET'>>;
export function apiRequest<
  Path extends OpenApiRoute,
  Method extends Exclude<OpenApiMethodsFor<Path>, 'GET'>,
>(path: Path, options: OpenApiRequestOptions<Path, Method, RequestOptions>): Promise<OpenApiDataFor<Path, Method>>;
export async function apiRequest(path: OpenApiRoute, options: RequestOptions = {}): Promise<unknown> {
  const payload = await apiRequestEnvelopeUnsafe<{ data: unknown } | undefined>(path, options);
  return payload?.data;
}

/** Typed envelope request, preserving pagination fields alongside `data`. */
export function apiRequestEnvelope<Path extends GetRoute>(
  path: Path,
  options?: OpenApiRequestOptions<Path, 'GET', RequestOptions>,
): Promise<OpenApiEnvelopeFor<Path, 'GET'>>;
export function apiRequestEnvelope<
  Path extends OpenApiRoute,
  Method extends Exclude<OpenApiMethodsFor<Path>, 'GET'>,
>(path: Path, options: OpenApiRequestOptions<Path, Method, RequestOptions>): Promise<OpenApiEnvelopeFor<Path, Method>>;
export async function apiRequestEnvelope(path: OpenApiRoute, options: RequestOptions = {}): Promise<unknown> {
  return apiRequestEnvelopeUnsafe(path, options);
}

async function apiRequestEnvelopeUnsafe<T>(
  path: OpenApiRoute,
  options: RequestOptions = {},
): Promise<T> {
  const { apiBaseUrl } = getEnv();
  const method = options.method ?? 'GET';
  const url = new URL(apiBaseUrl + path);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
  }

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (method !== 'GET') {
    const csrf = readCsrfToken();
    if (csrf) headers['x-csrf-token'] = csrf;
  }
  if (options.bookingAccessToken) headers['x-booking-access-token'] = options.bookingAccessToken;
  if (options.waitlistAccessToken) headers['x-waitlist-access-token'] = options.waitlistAccessToken;
  if (options.uploadToken) headers['x-upload-token'] = options.uploadToken;

  let response: Response;
  try {
    response = await fetch(url.toString(), {
      method,
      headers,
      credentials: 'include',
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (error) {
    if ((error as Error)?.name === 'AbortError') throw error;
    throw new ApiError(0, 'NETWORK_ERROR', 'We could not reach TripCircle. Check your connection.');
  }

  const requestId = response.headers.get('x-request-id') ?? undefined;
  if (response.status === 204) return undefined as T;

  let payload: unknown = null;
  const text = await response.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const body = payload as Partial<ApiErrorBody> | null;
    throw new ApiError(
      response.status,
      body?.error?.code ?? `HTTP_${response.status}`,
      body?.error?.message ?? 'Something went wrong',
      body?.error?.details,
      body?.error?.requestId ?? requestId,
    );
  }
  return payload as T;
}
